import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MultipleSelectedItemsBox from './MultipleSelectedItemsBox';
import { SwitchWithLevelOrCount, SearchBar } from '@bloomays-lib/ui.shared';
import useFuse from '../../customHooks/useFuse';

export interface IMultipleSelectItem {
  label: string;
  count?: number;
  level?: number;
  selected?: boolean;
  id: string;
  alternateLabels?: string[];
}

export type MultipleSelectSearchProps = {
  onSwitch: (e: IMultipleSelectItem, c: boolean) => void;
  onClickLevel?: (e: IMultipleSelectItem, level: number) => void;
  items?: IMultipleSelectItem[];
  search?: string;
  setSearch: (search: string) => void;
  onSearch?: (search: string) => void;
  loading?: boolean;
  setSelectedItemsList: React.Dispatch<React.SetStateAction<IMultipleSelectItem[]>>;
  selectedItemsList: IMultipleSelectItem[];
  onSwitchMultiple?: (items: IMultipleSelectItem[], unSelect?: boolean) => void;
  withLevel?: boolean;
};

const MultipleSelectSearch = ({
  items = [],
  onSwitch,
  onClickLevel,
  onSearch,
  loading = false,
  search,
  setSearch,
  selectedItemsList = [],
  setSelectedItemsList,
  onSwitchMultiple,
  withLevel = true,
}: MultipleSelectSearchProps) => {
  const { t } = useTranslation(['talentTool', 'random', 'talentSheet']);
  const [availableItems, setAvailableItems] = useState<IMultipleSelectItem[]>(items);
  const [selectAll, setSelectAll] = useState({
    level: undefined,
    check: false,
  });

  useEffect(() => {
    if (items.length > 0) setAvailableItems(items);
  }, [items]);

  const searchFuse = useFuse<IMultipleSelectItem>(items, {
    keys: ['label', 'alternateLabels'],
    threshold: 0.2,
    shouldSort: false,
    includeScore: true,
  });

  const handleChange = (item: IMultipleSelectItem, c: boolean) => {
    onSwitch(item, c);
    // !!! ce code m'a l'air pas nécessaire
    //if (item.selected) {
    //  setSelectedItemsList(selectedItemsList.filter((selected) => selected.label !== item.label));
    //} else {
    //  setSelectedItemsList([...selectedItemsList, item]);
    //}
  };

  const handleChangeMultiple = (items: IMultipleSelectItem[], isSelect: boolean) => {
    onSwitchMultiple && onSwitchMultiple(items, isSelect);

    if (isSelect) {
      setSelectedItemsList(items);
    } else {
      setSelectedItemsList([]);
      setSearch('');
    }
  };
  const inputReference = useRef<HTMLInputElement>(null);

  if (!onSearch) {
    onSearch = (searchValue: string) => {
      if (searchValue === '') {
        return setAvailableItems(items);
      }

      const res = searchFuse(searchValue);
      const fuseRes = res.map((r) => r.item);
      setAvailableItems(fuseRes);
    };
  }

  return (
    <>
      {onSearch && (
        <SearchBar
          placeholder={t('search')}
          searchValue={search}
          onChange={(e: any) => {
            onSearch?.(e.target?.value || '');
            setSearch(e.target?.value || '');
          }}
          ref={inputReference}
        />
      )}
      <MultipleSelectedItemsBox items={selectedItemsList} handleChange={handleChange} />

      {onSwitchMultiple && items.length > 0 && items.length < 20 && (
        <SwitchWithLevelOrCount
          checked={selectAll.check}
          label={selectAll.check ? t('unselectAll', { ns: 'talentSheet' }) : t('selectAll', { ns: 'talentSheet' })}
          onSwitch={() => {
            const isSelect = selectAll.check;
            setSelectAll({
              level: isSelect === true ? undefined : selectAll.level,
              check: !isSelect,
            });
            handleChangeMultiple(items, !isSelect);
          }}
        />
      )}
      {loading && t('loadingText', { ns: 'random' })}
      {availableItems &&
        availableItems.length > 0 &&
        availableItems.map((item: any) => (
          <SwitchWithLevelOrCount
            key={item?.label}
            checked={item?.selected}
            withLevel={withLevel}
            count={item.count}
            onClickLevel={
              onClickLevel
                ? (level: number) => {
                    setSelectedItemsList([
                      ...selectedItemsList.filter((el) => el.label !== item.label),
                      { ...item, level: level },
                    ]);
                    onClickLevel(item, level);
                  }
                : undefined
            }
            label={item?.label}
            onSwitch={(evt: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              handleChange(item, checked);
            }}
            levelSelected={item?.level}
          />
        ))}
    </>
  ) as React.ReactNode;
};

export default MultipleSelectSearch;
export { MultipleSelectSearch };
